import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  HourglassIcon,
  ArrowRight01Icon,
  Delete02Icon,
  Copy01Icon,
  CheckmarkCircle01Icon,
  Image01Icon,
  UserCircleIcon,
  FlashIcon,
  PlusSignIcon,
  PencilEdit02Icon,
  InformationCircleIcon,
  AlertCircleIcon,
  CancelCircleIcon,
  Money01Icon,
  Alert01Icon,
  SmartPhone01Icon,
  Mail01Icon,
  Refresh01Icon
} from '@hugeicons/core-free-icons';
import Button from '../../components/Button';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { FLAGS } from '../../config/flags';
import { useToast } from '../../contexts/ToastContext';
import { FadeLoader } from 'react-spinners';
import ResponsiveModal from '../../components/ResponsiveModal';
import { useAuth } from '../../contexts/AuthContext';
import BalanceSyncTutorialModal from '../../components/BalanceSyncTutorialModal';

const RequestTimer = ({ createdAt }) => {
  const [timeLeft, setTimeLeft] = useState(1200); // 20 mins default

  useEffect(() => {
    const created = new Date(createdAt).getTime();
    const expiry = created + (20 * 60 * 1000);
    
    const update = () => {
      const now = new Date().getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setTimeLeft(diff);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [createdAt]);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg font-mono font-black text-[10px] bg-red-500/10 text-red-500 ${timeLeft < 300 ? 'animate-pulse' : ''}`}>
      <HugeiconsIcon icon={HourglassIcon} size={10} />
      {mins}:{secs.toString().padStart(2, '0')}
    </div>
  );
};

const AgentOrders = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' | 'buckets'
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(0);

  const loadingMessages = [
    "Communicating with Deriv...",
    "Verifying transaction details...",
    "Processing secure transfer...",
    "Finalizing release..."
  ];
  
  const [requests, setRequests] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [agentBanks, setAgentBanks] = useState([]);
  const [isOrderLinkModalOpen, setIsOrderLinkModalOpen] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [isConfirmReleaseModalOpen, setIsConfirmReleaseModalOpen] = useState(false);
  const [isConfirmRejectModalOpen, setIsConfirmRejectModalOpen] = useState(false);
  const [isClientProfileModalOpen, setIsClientProfileModalOpen] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  
  const [releasingTx, setReleasingTx] = useState(null);
  const [rejectingTx, setRejectingTx] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [rejectedClient, setRejectedClient] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [editData, setEditData] = useState({ current_rate: '', bank_account_id: '', cashback_rate: '' });

  // Balance Sync Tutorial Modal
  const [showBalanceSyncTutorial, setShowBalanceSyncTutorial] = useState(false);
  const [hasCheckedTutorial, setHasCheckedTutorial] = useState(false);

  const handleViewClient = (client) => {
    setViewingClient(client);
    setIsClientProfileModalOpen(true);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const truncateName = (name, length = 10) => {
    if (!name) return 'Unknown';
    return name.length > length ? `${name.substring(0, length)}...` : name;
  };

  const [createOrderStep, setCreateOrderStep] = useState(1);
  const [orderFormData, setOrderFormData] = useState({
    transaction_type: 'deposit',
    current_rate: '',
    spread: '',
    total_amount_usd: '',
    min_amount_usd: '',
    bank_account_id: '',
    cashback_rate: ''
  });
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [newestLink, setNewestLink] = useState(null);
  const [showCopied, setShowCopied] = useState(false);

  useEffect(() => {
    let interval;
    if (processing) {
      interval = setInterval(() => {
        setProcessingMessage((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
    } else {
      setProcessingMessage(0);
    }
    return () => clearInterval(interval);
  }, [processing]);

  const fetchData = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const [bucketsRes, requestsRes, banksRes] = await Promise.allSettled([
        api.get('/agent/orders'),
        api.get('/agent/orders-with-transactions'),
        api.get('/bank-accounts')
      ]);

      if (bucketsRes.status === 'fulfilled') setBuckets(bucketsRes.value.data.orders || []);
      
      if (requestsRes.status === 'fulfilled') {
        const orders = requestsRes.value.data?.orders || [];
        const flattened = orders.flatMap(o => (o.transactions || []).map(tx => ({
          ...tx,
          order_token: o.link_token,
          transaction_type: o.transaction_type,
          order: o
        })));
        
        const pending = flattened.filter(tx => {
          if (['pending', 'pending_verification', 'processing'].includes(tx.status)) return true;
          if (tx.status === 'verified' && tx.transaction_type === 'withdrawal') return true;
          return false;
        });
        setRequests(pending);
      }

      if (banksRes.status === 'fulfilled') {
        const banksData = banksRes.value.data;
        const banks = Array.isArray(banksData) ? banksData : (banksData?.data || []);
        setAgentBanks(banks);
      }

    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 1500); 
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (['current_rate', 'spread', 'total_amount_usd', 'min_amount_usd', 'cashback_rate'].includes(name)) {
      let val = value.replace(/[^0-9.]/g, '');
      const parts = val.split('.');
      if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
      if (parts.length === 2 && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
      setOrderFormData({ ...orderFormData, [name]: val });
    } else {
      setOrderFormData({ ...orderFormData, [name]: value });
    }
  };

  const handleCreateOrderLink = async () => {
    setProcessing(true);
    try {
      const selectedBank = agentBanks.find(b => b.id.toString() === orderFormData.bank_account_id.toString());

      const payload = {
        transaction_type: orderFormData.transaction_type,
        current_rate: parseFloat(orderFormData.current_rate),
        spread: parseFloat(orderFormData.spread),
        total_amount_usd: parseFloat(orderFormData.total_amount_usd),
        min_amount_usd: parseFloat(orderFormData.min_amount_usd),
        bank_name: selectedBank?.bank_name || 'N/A',
        account_name: selectedBank?.account_name || 'N/A',
        account_number: selectedBank?.account_number || 'N/A',
        bank_account_id: orderFormData.bank_account_id,
        cashback_rate: orderFormData.cashback_rate ? (parseFloat(orderFormData.cashback_rate) / 100) : 0
      };

      const response = await api.post('/agent/orders', payload);
      const newOrder = response.data.order;
      addToast("Order bucket launched successfully!", "success");
      setNewestLink({ url: `${window.location.origin}/order/pay/${newOrder.link_token}` });
      
      setIsOrderLinkModalOpen(false);
      setCreateOrderStep(1);
      setOrderFormData({ transaction_type: 'deposit', current_rate: '', spread: '', total_amount_usd: '', min_amount_usd: '', bank_account_id: '', cashback_rate: '' });
      setIsLinkModalOpen(true);
      fetchData();
    } catch (err) {
      addToast(err.message || "Failed to create bucket", "error");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/order/pay/${token}`);
    addToast('Link copied to clipboard!', 'success');
  };

  const handleRemoveBucket = async (token) => {
    if (!window.confirm("Are you sure you want to remove this order bucket? It will no longer accept new payments.")) return;
    setProcessing(token);
    try {
      await api.delete(`/agent/orders/${token}`);
      addToast("Bucket removed successfully.", "success");
      fetchData();
    } catch (err) {
      addToast(err.message || "Failed to remove bucket", "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleEditBucket = (bucket) => {
    setSelectedBucket(bucket);
    setEditData({ 
      current_rate: bucket.current_rate, 
      bank_account_id: bucket.bank_account_id || '',
      cashback_rate: bucket.cashback_rate ? (parseFloat(bucket.cashback_rate) * 100).toString() : '0'
    });
    setIsEditModalOpen(true);
  };

  const submitEdit = async () => {
    setProcessing(true);
    try {
      const payload = {
        ...editData,
        cashback_rate: parseFloat(editData.cashback_rate) / 100
      };
      await api.put(`/agent/orders/${selectedBucket.link_token}`, payload);
      addToast("Bucket updated!", "success");
      setIsEditModalOpen(false);
      fetchData();
    } catch (err) {
      addToast(err.message || "Failed to update bucket", "error");
    }
    finally {
      setProcessing(false);
    }
  };

  const handleViewReceipt = (tx) => {
    if (tx.proof_of_payment_url) {
      setImageError(false);
      setReceiptUrl(tx.proof_of_payment_url);
      setIsReceiptModalOpen(true);
    } else {
      addToast("No receipt uploaded.", "error");
    }
  };

  const handleReject = (tx) => {
    setRejectingTx(tx);
    setIsConfirmRejectModalOpen(true);
  };

  const processReject = async () => {
    if (!rejectingTx) return;
    setProcessing(rejectingTx.uuid);
    setIsConfirmRejectModalOpen(false);
    try {
      await api.post(`/agent/transactions/${rejectingTx.uuid}/reject`);
      addToast("Transaction rejected. Capacity restored.", "success");
      setRejectedClient(rejectingTx.client);
      setIsContactModalOpen(true);
      fetchData();
    } catch (err) { addToast(err.message || "Failed to reject", "error"); }
    finally { setProcessing(false); setRejectingTx(null); }
  };

  const handleRelease = (tx) => {
    setReleasingTx(tx);
    if (tx.transaction_type === 'deposit') {
      setIsConfirmReleaseModalOpen(true);
    } else {
      setSelectedBucket(tx); 
      setPaymentProof(null);
      setIsReleaseModalOpen(true);
    }
  };
const processDepositRelease = async () => {
  if (!releasingTx) return;
  setProcessing(releasingTx.uuid);
  setIsConfirmReleaseModalOpen(false);
  try {
    await api.post(`/agent/orders/${releasingTx.order_token}/transfer`, {
      transaction_uid: releasingTx.uuid
    });
    addToast("Funds released successfully!", "success");
    // Show tutorial modal after successful release
    api.get('/balance-sync-tutorial/status')
      .then(res => {
        if (res.data.data.should_show) {
          setShowBalanceSyncTutorial(true);
        }
      })
      .catch(() => {});
    fetchData();
  } catch (err) {
    const msg = err.response?.data?.message || err.message || "Release failed";
    addToast(msg, "error");
  }
  finally { setProcessing(false); setReleasingTx(null); }
};

  const submitFiatPayment = async () => {
    setProcessing(selectedBucket.uuid);
    const formData = new FormData();
    if (paymentProof) {
      formData.append('receipt', paymentProof);
    }

    try {
      await api.postFile(`/agent/transactions/${selectedBucket.uuid}/confirm-fiat`, formData);
      addToast("Payment confirmed! Client notified.", "success");
      // Show tutorial modal after successful payment confirmation
      api.get('/balance-sync-tutorial/status')
        .then(res => {
          if (res.data.data.should_show) {
            setShowBalanceSyncTutorial(true);
          }
        })
        .catch(() => {});
      setIsReleaseModalOpen(false);
      fetchData();
    } catch (err) {
      addToast(err.message || "Failed to confirm payment", "error");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FadeLoader color="#4ade80" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Direct Orders</h1>
          <p className="text-muted-gray mt-1 font-medium">Manage your buckets and process client requests.</p>
        </div>
        
        <div className="bg-off-white dark:bg-gray-800 p-1.5 rounded-2xl flex gap-1 border border-light-gray/20 dark:border-gray-700 shadow-inner w-full md:w-auto">
          <button 
            onClick={() => setActiveTab('requests')}
            className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'requests' ? 'bg-white dark:bg-dark-surface shadow-md text-soft-green scale-[1.02]' : 'text-muted-gray hover:text-charcoal dark:hover:text-white'}`}
          >
            Requests ({requests.length})
          </button>
          <button 
            onClick={() => setActiveTab('buckets')}
            className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'buckets' ? 'bg-white dark:bg-dark-surface shadow-md text-soft-green scale-[1.02]' : 'text-muted-gray hover:text-charcoal dark:hover:text-white'}`}
          >
            Manage Links ({buckets.length})
          </button>
        </div>
      </div>

      {activeTab === 'requests' && requests.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 rounded-2xl flex items-center gap-3 text-yellow-700 dark:text-yellow-400 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="p-2 bg-yellow-500/20 rounded-xl shrink-0 text-yellow-600">
            <HugeiconsIcon icon={InformationCircleIcon} size={20} />
          </div>
          <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest">Verify payment and release funds immediately.</p>
        </div>
      )}

      {activeTab === 'requests' ? (
        <div className="grid gap-3">
          {requests.length === 0 ? (
            <div className="bg-white/50 dark:bg-dark-surface/50 p-20 rounded-[2.5rem] border-2 border-dashed border-light-gray/20 text-center">
              <HugeiconsIcon icon={HourglassIcon} size={48} className="mx-auto mb-4 text-muted-gray opacity-20" />
              <h3 className="text-xl font-black text-charcoal dark:text-white">All caught up!</h3>
              <p className="text-muted-gray mt-2">No pending requests to process.</p>
            </div>
          ) : (
            requests.map(tx => {
              let cashbackEarned = 0;
              if (tx.transaction_type === 'deposit' && parseFloat(tx.order?.cashback_rate || 0) > 0) {
                const rate = parseFloat(tx.order.current_rate || 1);
                const amountUsd = parseFloat(tx.amount_sent || 0) / rate;
                cashbackEarned = amountUsd * parseFloat(tx.order.spread || 0) * parseFloat(tx.order.cashback_rate);
              }

              return (
                <div key={tx.uuid} className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm hover:shadow-float transition-all overflow-hidden group">
                  <div className="p-4 sm:p-6">
                    {/* Header Row: Avatar, Name, Amount, Timer */}
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <button 
                        onClick={() => handleViewClient(tx.client)}
                        className="flex items-center gap-3 min-w-0 group/client"
                      >
                        <div className={`w-10 h-10 rounded-xl shrink-0 flex items-center justify-center font-black text-xs ${tx.transaction_type === 'deposit' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'}`}>
                          {getInitials(tx.client?.name || tx.client?.email)}
                        </div>
                        <div className="min-w-0 text-left">
                          <p className="font-black text-charcoal dark:text-white truncate text-sm group-hover/client:text-soft-green transition-colors">
                            {truncateName(tx.client?.name || tx.client?.email.split('@')[0])}
                          </p>
                          <p className="text-[9px] font-black uppercase text-muted-gray tracking-tighter">{tx.transaction_type} • {tx.reference.split('-').pop()}</p>
                        </div>
                      </button>
                      <div className="text-right">
                        <p className="text-lg font-black text-soft-green">₦{parseFloat(tx.amount_sent).toLocaleString()}</p>
                        <RequestTimer createdAt={tx.created_at} />
                      </div>
                    </div>

                    {/* Meta Row: USD Value & Rate */}
                    <div className="flex items-center justify-between text-[10px] font-bold text-muted-gray mb-4 px-1">
                      <div className="flex gap-4">
                        <span>${(parseFloat(tx.amount_sent) / parseFloat(tx.order?.current_rate || 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</span>
                        <span className="opacity-30">|</span>
                        <span>₦{parseFloat(tx.order?.current_rate || 0).toLocaleString()}/$</span>
                      </div>
                      {tx.proof_of_payment_url && (
                        <button onClick={() => handleViewReceipt(tx)} className="text-soft-green flex items-center gap-1 hover:underline font-black uppercase tracking-tighter">
                          <HugeiconsIcon icon={Image01Icon} size={12} />
                          Receipt
                        </button>
                      )}
                    </div>

                    {/* Reward Box */}
                    {cashbackEarned > 0 && (
                      <div className="mb-4 p-3 bg-purple-500/10 rounded-xl border border-purple-500/20 flex items-center gap-2 text-purple-600 dark:text-purple-400">
                        <HugeiconsIcon icon={Money01Icon} size={14} className="shrink-0" />
                        <p className="text-[10px] font-bold">Client reward: <b>₦{cashbackEarned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></p>
                      </div>
                    )}

                    {/* Actions Row */}
                    <div className="flex gap-2">
                      {FLAGS.ENABLE_REJECT_FEATURE && (
                        <button 
                          onClick={() => handleReject(tx)} 
                          className="px-5 py-3 bg-red-500 text-white hover:bg-red-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-red-500/20"
                        >
                          Reject
                        </button>
                      )}
                      <Button 
                        onClick={() => handleRelease(tx)} 
                        disabled={processing === tx.uuid || tx.status === 'processing'} 
                        className="flex-1 py-3 rounded-xl font-black text-xs shadow-lg gap-2"
                      >
                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} />
                        {tx.status === 'processing' ? 'Processing...' : (tx.transaction_type === 'withdrawal' ? 'Pay Naira' : 'Pay USD')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {buckets.length === 0 ? (
            <div className="col-span-full bg-white/50 dark:bg-dark-surface/50 p-20 rounded-[3rem] border-2 border-dashed border-light-gray/20 dark:border-dark-border/20 text-center flex flex-col items-center">
              <div className="bg-soft-green/10 p-6 rounded-[2.5rem] mb-6 text-soft-green">
                <HugeiconsIcon icon={PlusSignIcon} size={48} />
              </div>
              <h3 className="text-2xl font-black text-charcoal dark:text-white mb-2">No Active Links</h3>
              <p className="text-muted-gray mb-8 max-w-sm mx-auto">You haven&apos;t created any liquidity buckets yet. Create one to start receiving orders.</p>
              <Button onClick={() => setIsOrderLinkModalOpen(true)} className="px-10 py-4 rounded-2xl font-black shadow-lg">
                Create First Link
              </Button>
            </div>
          ) : (
            <>
              {buckets.map(bucket => {
                const percent = Math.min((parseFloat(bucket.filled_amount_usd) / parseFloat(bucket.total_amount_usd)) * 100, 100);
                return (
                  <div key={bucket.link_token} className="bg-white dark:bg-dark-surface p-6 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float flex flex-col justify-between group">
                  <div>
                    <div className="flex justify-between items-start mb-6">
                      <span className={`text-[10px] px-3 py-1 rounded-full font-black uppercase ${bucket.transaction_type === 'deposit' ? 'bg-blue-500/10 text-blue-600' : 'bg-purple-500/10 text-purple-600'}`}>
                        {bucket.transaction_type}
                      </span>
                      <div className="flex gap-1 transition-opacity">
                        <button onClick={() => handleEditBucket(bucket)} className="p-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors text-muted-gray"><HugeiconsIcon icon={PencilEdit02Icon} size={18} /></button>
                        <button onClick={() => handleRemoveBucket(bucket.link_token)} className="p-2 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 text-red-400 rounded-xl transition-colors"><HugeiconsIcon icon={Delete02Icon} size={18} /></button>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-black text-charcoal dark:text-white font-mono mb-1">{bucket.link_token}</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <p className="text-xs font-black text-muted-gray uppercase tracking-widest">Rate: ₦{bucket.current_rate}/$</p>
                      {bucket.transaction_type === 'deposit' && parseFloat(bucket.cashback_rate) > 0 && (
                        <span className="text-[8px] font-black text-purple-500 uppercase bg-purple-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                          <HugeiconsIcon icon={Money01Icon} size={10} />
                          Cashback Enabled ({(parseFloat(bucket.cashback_rate) * 100).toFixed(0)}%)
                        </span>
                      )}
                    </div>

                    {bucket.bank_details && (
                      <div className="mb-6 p-3 bg-off-white dark:bg-gray-800/50 rounded-2xl border border-light-gray/10">
                        <p className="text-[8px] font-black text-muted-gray uppercase tracking-widest mb-1">Receiving Bank</p>
                        <p className="text-[10px] font-bold text-charcoal dark:text-white truncate">{bucket.bank_details.bank_name}</p>
                        <p className="text-[10px] font-mono text-muted-gray truncate">{bucket.bank_details.account_number}</p>
                      </div>
                    )}

                    <div className="space-y-2 mb-8">
                      <div className="flex justify-between text-[10px] font-black uppercase text-muted-gray">
                        <span>Filled: ${parseFloat(bucket.filled_amount_usd).toFixed(0)}</span>
                        <span>Cap: ${parseFloat(bucket.total_amount_usd).toFixed(0)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full ${percent >= 100 ? 'bg-muted-gray' : 'bg-soft-green'} transition-all duration-1000`} style={{ width: `${percent}%` }}></div>
                      </div>
                    </div>
                  </div>

                  <Button onClick={() => handleCopyLink(bucket.link_token)} variant="secondary" className="w-full py-3.5 rounded-2xl gap-2 font-black text-xs">
                    <HugeiconsIcon icon={Copy01Icon} size={16} />
                    Copy Link
                  </Button>
                </div>
              );
            })}
              
              <button
                onClick={() => setIsOrderLinkModalOpen(true)}
                className="bg-white/50 dark:bg-dark-surface/50 p-6 rounded-[2.5rem] border-2 border-dashed border-light-gray/20 dark:border-dark-border/20 shadow-float flex flex-col items-center justify-center gap-4 group hover:border-soft-green/50 hover:bg-soft-green/5 transition-all min-h-[280px]"
              >
                <div className="w-16 h-16 rounded-full bg-soft-green/10 flex items-center justify-center text-soft-green group-hover:scale-110 transition-transform">
                  <HugeiconsIcon icon={PlusSignIcon} size={32} />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-black text-charcoal dark:text-white mb-1">Create New Link</h3>
                  <p className="text-sm text-muted-gray font-medium">Add another liquidity bucket</p>
                </div>
              </button>
            </>
          )}
        </div>
      )}

      {activeTab === 'buckets' && (
        <button
          onClick={() => setIsOrderLinkModalOpen(true)}
          className="fixed bottom-24 right-6 sm:bottom-10 sm:right-10 w-16 h-16 bg-soft-green text-black rounded-full shadow-float-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-all z-40 group lg:hidden animate-in zoom-in-50 duration-300"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={28} className="font-black" />
          <div className="absolute -top-12 right-0 bg-charcoal dark:bg-dark-surface text-white text-[10px] font-black px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"> Launch New Bucket </div>
        </button>
      )}

      <ResponsiveModal isOpen={isConfirmRejectModalOpen} onClose={() => setIsConfirmRejectModalOpen(false)} title="Reject Transaction?">
        <div className="py-6 space-y-8 text-center">
          <div className="bg-red-500/10 p-8 rounded-full inline-block text-red-500 animate-bounce-slow"><HugeiconsIcon icon={Alert01Icon} size={48} /></div>
          <div>
            <h3 className="text-2xl font-black text-charcoal dark:text-white mb-2">Are you sure?</h3>
            <p className="text-muted-gray max-w-xs mx-auto text-sm">Rejecting this transaction will restore the bucket capacity and notify the client. This action is irreversible.</p>
          </div>
          <div className="bg-off-white dark:bg-gray-800 p-4 rounded-2xl text-left border border-red-100 dark:border-red-900/20 mx-4">
            <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Client</p>
            <p className="font-bold text-charcoal dark:text-white">{rejectingTx?.client?.name || rejectingTx?.client?.email}</p>
            <p className="text-lg font-black text-soft-green mt-2">₦{parseFloat(rejectingTx?.amount_sent || 0).toLocaleString()}</p>
          </div>
          <div className="flex gap-3 px-4">
            <Button variant="secondary" onClick={() => setIsConfirmRejectModalOpen(false)} className="flex-1 py-4 rounded-2xl font-black">Go Back</Button>
            <Button onClick={processReject} disabled={processing} className="flex-1 py-4 rounded-2xl font-black bg-red-500 text-white border-none shadow-lg">Confirm Reject</Button>
          </div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isClientProfileModalOpen} onClose={() => setIsClientProfileModalOpen(false)}>
        <div className="py-2">
          <div className="bg-gradient-to-br from-soft-green/20 to-soft-green/5 p-8 rounded-[2.5rem] border border-soft-green/20 text-center relative overflow-hidden mb-6 mx-4">
            <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12"><HugeiconsIcon icon={UserCircleIcon} size={120} /></div>
            <div className="w-24 h-24 bg-white dark:bg-dark-surface rounded-[2rem] flex items-center justify-center text-soft-green font-black text-3xl mx-auto mb-4 shadow-float-sm border-4 border-white dark:border-dark-surface relative z-10">{getInitials(viewingClient?.name || viewingClient?.email)}</div>
            <div className="relative z-10">
              <h3 className="text-2xl font-black text-charcoal dark:text-white mb-1 leading-tight">{viewingClient?.name || 'Private User'}</h3>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/50 dark:bg-black/20 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-soft-green animate-pulse"></span>
                <span className="text-[10px] font-black uppercase text-muted-gray tracking-widest">Active Client</span>
              </div>
            </div>
          </div>
          <div className="space-y-3 px-2 mx-4">
            <div className="p-5 bg-off-white dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 flex items-center gap-4 group hover:bg-white dark:hover:bg-gray-800 transition-all">
              <div className="p-3 bg-white dark:bg-dark-surface rounded-2xl text-muted-gray shadow-sm group-hover:text-soft-green transition-colors"><HugeiconsIcon icon={Mail01Icon} size={20} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase text-muted-gray tracking-[0.2em] mb-0.5">Email Address</p>
                <p className="font-bold text-charcoal dark:text-white truncate text-sm">{viewingClient?.email || 'N/A'}</p>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(viewingClient?.email); addToast('Email copied!', 'success'); }} className="p-2 text-muted-gray hover:text-soft-green"><HugeiconsIcon icon={Copy01Icon} size={16} /></button>
            </div>
            <div className="p-5 bg-off-white dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 flex items-center gap-4 group hover:bg-white dark:hover:bg-gray-800 transition-all">
              <div className="p-3 bg-white dark:bg-dark-surface rounded-2xl text-muted-gray shadow-sm group-hover:text-soft-green transition-colors"><HugeiconsIcon icon={SmartPhone01Icon} size={20} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase text-muted-gray tracking-[0.2em] mb-0.5">Phone Number</p>
                <p className="font-bold text-charcoal dark:text-white text-sm">{viewingClient?.phone || viewingClient?.primary_phone?.full_phone_number || 'No Phone Linked'}</p>
              </div>
              {(viewingClient?.phone || viewingClient?.primary_phone?.full_phone_number) && (
                <button onClick={() => { const p = viewingClient?.phone || viewingClient?.primary_phone?.full_phone_number; navigator.clipboard.writeText(p); addToast('Phone copied!', 'success'); }} className="p-2 text-muted-gray hover:text-soft-green"><HugeiconsIcon icon={Copy01Icon} size={16} /></button>
              )}
            </div>
          </div>
          <div className="px-4"><Button onClick={() => setIsClientProfileModalOpen(false)} className="w-full mt-8 py-4 rounded-2xl font-black shadow-lg">Dismiss</Button></div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Order Link">
        <div className="py-4 space-y-6">
          <div>
            <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Exchange Rate (₦/$)</label>
            <input type="number" value={editData.current_rate} onChange={(e) => setEditData({...editData, current_rate: e.target.value})} className="w-full px-5 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-xl text-charcoal dark:text-white focus:ring-2 focus:ring-soft-green" />
          </div>
          <div>
            <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Receiving Bank</label>
            <select value={editData.bank_account_id} onChange={(e) => setEditData({...editData, bank_account_id: e.target.value})} className="w-full px-5 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white">
              <option value="">Select Bank Account</option>
              {agentBanks.map(b => (<option key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</option>))}
            </select>
          </div>
          {selectedBucket?.transaction_type === 'deposit' && (
            <div>
              <label className="block text-[10px] font-black text-soft-green uppercase tracking-widest mb-2 px-1">Cashback Share (%)</label>
              <div className="relative">
                <input type="text" inputMode="decimal" value={editData.cashback_rate} onChange={(e) => { let val = e.target.value.replace(/[^0-9.]/g, ''); setEditData({...editData, cashback_rate: val}); }} className="w-full pl-5 pr-12 py-4 bg-soft-green/5 dark:bg-soft-green/10 border-2 border-soft-green/20 rounded-2xl outline-none font-black text-xl text-soft-green" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-black text-soft-green">%</span>
              </div>
            </div>
          )}
          <Button onClick={submitEdit} disabled={processing} className="w-full py-4 rounded-2xl font-black">{processing ? 'Saving...' : 'Update Link Details'}</Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isReceiptModalOpen} onClose={() => setIsReceiptModalOpen(false)} title="Payment Receipt">
        <div className="py-4 space-y-6">
          {!imageError ? (
            <div className="bg-off-white dark:bg-gray-800 rounded-3xl overflow-hidden border border-light-gray/20 dark:border-gray-700 shadow-inner"><img src={receiptUrl?.url || receiptUrl} alt="Receipt" className="w-full h-auto max-h-[60vh] object-contain mx-auto" onError={() => setImageError(true)} /></div>
          ) : (
            <div className="bg-off-white dark:bg-gray-800 p-12 rounded-[2.5rem] border border-dashed border-light-gray/30 dark:border-gray-700 text-center">
              <div className="bg-blue-500/10 p-6 rounded-full inline-block mb-4 text-blue-500"><HugeiconsIcon icon={Image01Icon} size={48} /></div>
              <h3 className="text-xl font-black text-charcoal dark:text-white mb-2">Attachment Preview</h3>
              <p className="text-muted-gray text-sm font-medium">This file type cannot be previewed directly. Please open it in a new tab.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mx-4">
            <Button onClick={() => window.open(receiptUrl?.url, '_blank')} variant="secondary" className="w-full py-4 rounded-2xl font-bold gap-2"><HugeiconsIcon icon={ArrowRight01Icon} size={18} className="-rotate-45" />Open in New Tab</Button>
            <Button onClick={() => setIsReceiptModalOpen(false)} className="w-full py-4 rounded-2xl font-black">Close</Button>
          </div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} title="Transaction Rejected">
        <div className="py-6 text-center">
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-full inline-block mb-4"><HugeiconsIcon icon={Delete02Icon} size={48} className="text-red-500" /></div>
          <h3 className="text-xl font-black text-charcoal dark:text-white mb-2">Please Contact Client</h3>
          <p className="text-muted-gray text-sm mb-6">Resolve the issue or process a refund manually.</p>
          <div className="bg-off-white dark:bg-gray-800 p-6 rounded-3xl border border-light-gray/20 dark:border-gray-700/50 text-left space-y-4">
            <div><p className="text-[10px] font-black uppercase text-muted-gray tracking-widest">Client Name</p><p className="font-bold text-charcoal dark:text-white">{rejectedClient?.name || 'Unknown'}</p></div>
            {rejectedClient?.phone && (<div><p className="text-[10px] font-black uppercase text-muted-gray tracking-widest">Phone Number</p><p className="font-bold text-charcoal dark:text-white select-all">{rejectedClient?.phone}</p></div>)}
          </div>
          <Button onClick={() => setIsContactModalOpen(false)} className="w-full mt-6 py-4 rounded-2xl font-black">Close</Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isReleaseModalOpen} onClose={() => setIsReleaseModalOpen(false)} title="Confirm Fiat Transfer">
        <div className="py-4 space-y-6">
          <div className="bg-purple-50 dark:bg-purple-900/10 p-6 rounded-3xl border border-purple-100 dark:border-purple-800/30 text-center">
            <p className="text-[10px] font-black uppercase text-purple-800 dark:text-purple-200 tracking-widest mb-1">Amount to Pay</p>
            <p className="text-3xl font-black text-charcoal dark:text-white">₦{selectedBucket ? parseFloat(selectedBucket.amount_sent).toLocaleString() : '0.00'}</p>
          </div>
          <div className="space-y-4">
            <p className="text-xs font-bold text-muted-gray uppercase tracking-widest px-1">Client Bank Details</p>
            <div className="bg-off-white dark:bg-gray-800 p-4 rounded-2xl border border-light-gray/20 dark:border-gray-700/50 space-y-3">
              <div className="flex justify-between items-center"><span className="text-xs font-bold text-muted-gray">Bank Name</span><span className="font-black text-charcoal dark:text-white">{selectedBucket?.bank_account?.bank_name}</span></div>
              <div className="flex justify-between items-center"><span className="text-xs font-bold text-muted-gray">Account Name</span><span className="font-black text-charcoal dark:text-white text-right">{selectedBucket?.bank_account?.account_name}</span></div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700"><span className="text-xs font-bold text-muted-gray">Account Number</span><div className="flex items-center gap-2"><span className="font-mono font-black text-lg text-charcoal dark:text-white">{selectedBucket?.bank_account?.account_number}</span><button onClick={() => {navigator.clipboard.writeText(selectedBucket?.bank_account?.account_number); addToast('Copied!', 'success')}} className="text-soft-green"><HugeiconsIcon icon={Copy01Icon} size={16} /></button></div></div>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-muted-gray uppercase tracking-widest px-1 mb-2">Proof of Payment</p>
            {paymentProof ? (
              <div className="relative w-full h-48 rounded-2xl overflow-hidden border border-light-gray/20 mb-3"><img src={URL.createObjectURL(paymentProof)} alt="Preview" className="w-full h-full object-cover" /><button onClick={() => setPaymentProof(null)} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg"><HugeiconsIcon icon={Delete02Icon} size={16} /></button></div>
            ) : (
              <label className="w-full py-4 rounded-2xl border-2 border-dashed flex items-center justify-center gap-2 cursor-pointer transition-all border-gray-200 dark:border-gray-700 text-muted-gray hover:bg-gray-50 dark:hover:bg-gray-800"><input type="file" accept="image/*" className="hidden" onChange={(e) => setPaymentProof(e.target.files[0])} /><HugeiconsIcon icon={Image01Icon} size={20} /><span className="font-bold text-sm">Upload Transfer Receipt (Optional)</span></label>
            )}
          </div>
          <Button onClick={submitFiatPayment} disabled={processing} className="w-full py-4 rounded-2xl font-black shadow-lg">{processing ? 'Confirming...' : 'I Have Paid'}</Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isConfirmReleaseModalOpen} onClose={() => setIsConfirmReleaseModalOpen(false)} title="Confirm Release">
        <div className="py-6 text-center space-y-6">
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-full inline-block text-red-500"><HugeiconsIcon icon={FlashIcon} size={48} /></div>
          <div><h3 className="text-xl font-black text-charcoal dark:text-white mb-2">Irreversible Action</h3><p className="text-muted-gray px-4 text-sm font-medium leading-relaxed">You are about to release <b>${releasingTx ? parseFloat(releasingTx.amount_sent / parseFloat(releasingTx.order?.current_rate)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</b> to <b>{releasingTx?.client?.name || releasingTx?.client?.email}</b>.</p></div>
          <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-2xl border border-red-100 dark:border-red-800/30"><p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest leading-tight">Please confirm that you have received the exact Naira amount in your bank account before proceeding.</p></div>
          <div className="flex gap-3 px-4"><Button variant="secondary" onClick={() => setIsConfirmReleaseModalOpen(false)} className="flex-1 py-4 rounded-2xl font-bold">Cancel</Button><Button onClick={processDepositRelease} disabled={processing} className="flex-[2] py-4 rounded-2xl font-black bg-red-500 hover:bg-red-600 text-white border-none shadow-lg">{processing ? 'Releasing...' : 'Confirm Release'}</Button></div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} title="Bucket Link Live">
        <div className="py-6 text-center px-4">
          <div className="bg-soft-green/10 p-6 rounded-[2rem] inline-block mb-6"><HugeiconsIcon icon={FlashIcon} size={48} className="text-soft-green" /></div>
          <h3 className="text-2xl font-black text-charcoal dark:text-white mb-4">Bucket Ready</h3>
          <div className="bg-off-white dark:bg-gray-800 p-4 rounded-2xl border font-mono text-xs mb-8 break-all text-charcoal dark:text-white shadow-inner mx-4">{newestLink?.url}</div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => copyToClipboard(newestLink?.url)} className="w-full py-4 rounded-2xl gap-2 font-black shadow-lg"><HugeiconsIcon icon={showCopied ? CheckmarkCircle01Icon : Copy01Icon} size={20} className="mr-2" />{showCopied ? 'Copied!' : 'Copy Link'}</Button>
            <Button variant="secondary" onClick={() => setIsLinkModalOpen(false)} className="w-full py-4 rounded-2xl font-black">Done</Button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Carousel Creation Modal */}
      <ResponsiveModal
        isOpen={isOrderLinkModalOpen}
        onClose={() => { setIsOrderLinkModalOpen(false); setCreateOrderStep(1); }}
        title={`Create Order Bucket (Step ${createOrderStep}/4)`}
      >
        <div className="py-4 space-y-6">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`h-1 flex-1 rounded-full ${createOrderStep >= s ? 'bg-soft-green' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
            ))}
          </div>

          {createOrderStep === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Transaction Type</label>
                <select name="transaction_type" value={orderFormData.transaction_type} onChange={handleInputChange} className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white">
                  <option value="deposit">Deposit (Client pays NGN)</option>
                  <option value="withdrawal">Withdrawal (Agent pays NGN)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Rate (₦/$)</label>
                  <input name="current_rate" type="number" min="0" step="0.01" required value={orderFormData.current_rate} onChange={handleInputChange} placeholder="1650" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Spread (₦)</label>
                  <input name="spread" type="number" min="0" step="0.01" required value={orderFormData.spread} onChange={handleInputChange} placeholder="40" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white" />
                </div>
              </div>
              {orderFormData.transaction_type === 'deposit' && (
                <div className="animate-in fade-in zoom-in-95 duration-300">
                  <label className="block text-[10px] font-black text-soft-green uppercase tracking-widest mb-2 px-1">Cashback Share (%)</label>
                  <div className="relative">
                    <input name="cashback_rate" type="text" inputMode="decimal" value={orderFormData.cashback_rate} onChange={handleInputChange} placeholder="5" className="w-full pl-4 pr-12 py-4 bg-soft-green/5 dark:bg-soft-green/10 border-2 border-soft-green/20 rounded-2xl outline-none font-black text-xl text-soft-green placeholder:text-soft-green/30" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 font-black text-soft-green">%</span>
                  </div>
                </div>
              )}
              <Button onClick={() => setCreateOrderStep(2)} className="w-full py-4 rounded-2xl mt-4 font-black">Next: Set Limits <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
            </div>
          )}

          {createOrderStep === 2 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Total Capacity (USD)</label>
                <input name="total_amount_usd" type="number" min="0" step="0.01" required value={orderFormData.total_amount_usd} onChange={handleInputChange} placeholder="5000" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Minimum Transaction (USD)</label>
                <input name="min_amount_usd" type="number" min="0" step="0.01" required value={orderFormData.min_amount_usd} onChange={handleInputChange} placeholder="100" className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setCreateOrderStep(1)} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
                <Button onClick={() => setCreateOrderStep(3)} className="flex-1 py-4 rounded-2xl font-black">Next: Select Bank <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
              </div>
            </div>
          )}

          {createOrderStep === 3 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2 px-1">Receiving Bank Account</label>
                <select name="bank_account_id" value={orderFormData.bank_account_id} onChange={handleInputChange} className="w-full px-4 py-4 bg-off-white dark:bg-gray-800 border-none rounded-2xl outline-none font-bold text-charcoal dark:text-white">
                  <option value="">Select a bank account</option>
                  {agentBanks.map(b => (
                    <option key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</option>
                  ))}
                </select>
              </div>
              {orderFormData.bank_account_id && (
                <div className="p-4 bg-off-white dark:bg-gray-800 rounded-2xl border border-light-gray/20 dark:border-gray-700 space-y-2">
                  {(() => {
                    const bank = agentBanks.find(b => b.id.toString() === orderFormData.bank_account_id.toString());
                    if (!bank) return null;
                    return (
                      <>
                        <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Bank Name</span><span className="text-sm font-bold text-charcoal dark:text-white">{bank.bank_name}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Account Name</span><span className="text-sm font-bold text-charcoal dark:text-white">{bank.account_name}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Account Number</span><span className="text-sm font-mono font-black text-charcoal dark:text-white">{bank.account_number}</span></div>
                      </>
                    );
                  })()}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setCreateOrderStep(2)} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
                <Button onClick={() => setCreateOrderStep(4)} className="flex-1 py-4 rounded-2xl font-black">Next: Review <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
              </div>
            </div>
          )}

          {createOrderStep === 4 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="p-4 bg-off-white dark:bg-gray-800 rounded-2xl border border-light-gray/20 dark:border-gray-700 space-y-3">
                <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Transaction Type</span><span className="text-sm font-black text-charcoal dark:text-white capitalize">{orderFormData.transaction_type}</span></div>
                <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Rate</span><span className="text-sm font-black text-charcoal dark:text-white">₦{orderFormData.current_rate}/$</span></div>
                <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Spread</span><span className="text-sm font-black text-charcoal dark:text-white">₦{orderFormData.spread}</span></div>
                <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Capacity</span><span className="text-sm font-black text-charcoal dark:text-white">${orderFormData.total_amount_usd}</span></div>
                <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Min Transaction</span><span className="text-sm font-black text-charcoal dark:text-white">${orderFormData.min_amount_usd}</span></div>
                {orderFormData.transaction_type === 'deposit' && parseFloat(orderFormData.cashback_rate || 0) > 0 && (
                  <div className="flex justify-between"><span className="text-[10px] font-black text-muted-gray uppercase">Cashback</span><span className="text-sm font-black text-soft-green">{orderFormData.cashback_rate}%</span></div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setCreateOrderStep(3)} className="flex-1 py-4 rounded-2xl font-black">Back</Button>
                <Button onClick={handleCreateOrderLink} disabled={processing} className="flex-1 py-4 rounded-2xl font-black bg-soft-green hover:bg-soft-green/90 text-black shadow-lg">{processing ? 'Creating...' : 'Create Bucket'}</Button>
              </div>
            </div>
          )}
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={!!processing && processing !== 'Saving...'} onClose={() => {}} title="Processing">
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <FadeLoader color="#4ade80" /><h3 className="text-xl font-black text-charcoal dark:text-white mt-8 mb-2 animate-pulse">{loadingMessages[processingMessage]}</h3><p className="text-muted-gray text-sm">Please do not close this window.</p>
        </div>
      </ResponsiveModal>

      {/* Balance Sync Tutorial Modal */}
      <BalanceSyncTutorialModal
        isOpen={showBalanceSyncTutorial}
        onClose={() => setShowBalanceSyncTutorial(false)}
        onDismissed={() => {}}
      />
    </div>
  );
};

export default AgentOrders;
